<template>
    <div class="app-wrapper">
        <header>
            <nav class="navbar">
                <router-link to="/">
                    <img class="main-logo" src="@/assets/full_logo.svg" alt="Logo" />
                </router-link>
            </nav>
        </header>
        <div class="care-main-page">
            <LoadingSpinner :isLoading="isLoading" />

            <div class="page">

                <!-- MODAL OVERLAY -->
                <div class="modal-overlay" :class="{ active: showModal }" @click="closeModal"></div>

                <!-- MODAL -->
                <div class="modal-content desktop" :class="{ active: showModal }" v-if="currentCaregiver">

                    <!-- CLOSE BUTTON -->
                    <button class="close-btn" @click="closeModal">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#17865F" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>

                    <!-- HEADER: Avatar + Name + Verified + Type + WhatsApp -->
                    <div class="modal-header">
                        <div class="modal-avatar">
                            <div class="avatar-photo-box-modal"
                                :style="{ background: caregiver && caregiver.available ? '#E8F5F0' : '#FFF8E8' }">
                                <img v-if="!currentCaregiver.imageError && currentCaregiver.image"
                                    :src="currentCaregiver.image" class="avatar-photo-modal"
                                    @error="currentCaregiver.imageError = true" />

                                <svg v-else viewBox="0 0 100 100" class="avatar-svg">
                                    <rect width="100" height="100"
                                        :fill="currentCaregiver.available ? '#FFF8E8' : '#E8F5F0'" />
                                    <circle cx="50" cy="42" r="20" fill="#D4A574" />
                                    <ellipse cx="50" cy="82" rx="26" ry="20" fill="#17865F" />
                                    <path d="M32 35 Q34 20 50 18 Q66 20 68 35" fill="#2D2D2D" />
                                </svg>
                            </div>
                        </div>


                        <div class="modal-info">
                            <div class="modal-name-badge">
                                <h2 class="modal-name">{{ currentCaregiver.name }}</h2>
                                <svg v-if="currentCaregiver.verified" width="20" height="20" viewBox="0 0 16 16"
                                    fill="none">
                                    <circle cx="8" cy="8" r="7" fill="#17865F" />
                                    <path d="M5 8L7 10L11 6" stroke="white" stroke-width="1.5" />
                                </svg>
                            </div>

                            <p class="modal-type">{{ caregiverType(currentCaregiver) }}</p>
                            <button class="modal-request-btn" @click="requestFromModal">Request on WhatsApp</button>
                        </div>
                    </div>

                    <!-- MODAL DETAILS -->
                    <div class="modal-details">
                        <!-- Education -->
                        <div class="detail-card">
                            <h3 class="detail-title">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#17865F"
                                    stroke-width="2">
                                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                                    <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                                </svg>
                                Education
                            </h3>
                            <p class="detail-text">
                                <span v-for="edu in currentCaregiver.educations" :key="edu.id">
                                    • {{ edu.title }}<br />
                                </span>
                            </p>
                        </div>

                        <!-- Speciality -->
                        <div class="detail-card">
                            <h3 class="detail-title">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#17865F"
                                    stroke-width="2">
                                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                                </svg>
                                Speciality
                            </h3>
                            <p class="detail-text">
                                <span v-for="sp in currentCaregiver.specialities" :key="sp.id">
                                    • {{ sp.name }}<br />
                                </span>
                            </p>
                        </div>

                        <!-- Experience -->
                        <div class="detail-card">
                            <h3 class="detail-title">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#17865F"
                                    stroke-width="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2"></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                </svg>
                                Experience
                            </h3>
                            <p class="detail-text">
                                {{ currentCaregiver.experience_years }}+ Years Professional Care Experience
                            </p>
                        </div>
                    </div>

                </div>

                <!-- HEADER -->
                <div class="header">
                    <h1>Choose a Care Giver</h1>
                </div>

                <!-- FILTERS -->
                <div class="filters">
                    <button v-for="f in filters" :key="f.key" class="filter-btn"
                        :class="{ active: activeFilter === f.key }" @click="toggleFilter(f.key)">
                        <!-- green dot for "available" -->
                        <span v-if="f.key === 'available'" class="status-dot-dot"></span>
                        {{ f.label }}
                    </button>
                </div>

                <!-- GRID: CAREGIVER CARDS -->
                <main class="grid">
                    <div v-for="caregiver in visibleCaregivers" :key="caregiver.id" class="care-card">
                        <div class="avatar-wrapper">
                            <div class="avatar-photo-box"
                                :style="{ background: caregiver && caregiver.available ? '#FFF8E8' : '#E8F5F0' }">

                                <img v-if="!caregiver.imageError && caregiver.image" :src="caregiver.image"
                                    alt="Caregiver" class="avatar-photo" @error="caregiver.imageError = true" />

                                <!-- Fallback SVG (former avatar) -->
                                <svg v-else viewBox="0 0 100 100" class="avatar-svg">
                                    <rect width="100" height="100"
                                        :fill="caregiver && caregiver.available ? '#FFF8E8' : '#E8F5F0'" />
                                    <circle cx="50" cy="42" r="20" fill="#D4A574" />
                                    <ellipse cx="50" cy="82" rx="26" ry="20" fill="#17865F" />
                                    <path d="M32 35 Q34 20 50 18 Q66 20 68 35" fill="#2D2D2D" />
                                </svg>
                            </div>


                            <div v-if="caregiver.verified" class="verified-badge">
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                    <circle cx="8" cy="8" r="7" fill="#17865F" />
                                    <path d="M5 8L7 10L11 6" stroke="white" stroke-width="1.5" />
                                </svg>
                                <span>Verified</span>
                            </div>
                        </div>

                        <div class="card-body">
                            <div class="card-header">
                                <h3>{{ caregiver.name }}</h3>
                                <button class="view-btn" @click="openModal(caregiver)">View Details</button>
                            </div>

                            <p class="care-type">{{ caregiverType(caregiver) }}</p>

                            <div class="status">
                                <span class="status-dot" :class="caregiver.available ? 'online' : 'offline'"></span>
                                <span class="status-text">{{ caregiver.available ? 'Available' : 'Unavailable' }}</span>
                            </div>

                            <button class="request-btn" @click="requestCaregiver(caregiver.name)">Request</button>
                        </div>
                    </div>
                </main>

                <!-- VIEW MORE -->
                <div class="view-more">
                    <button v-if="canViewMore" @click="loadMore" class="view-more-btn">
                        View Other Care Givers
                    </button>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import Request from "@/utils/apiclient.js";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

export default {
    name: "CareGivers",
    components: {
        LoadingSpinner
    },
    data() {
        return {
            caregivers: [],
            visibleCount: 12,
            activeFilter: null,
            showModal: false,
            currentCaregiver: null,
            isLoading: true,
            filters: [
                { key: "live-in", label: "Live-in" },
                { key: "daily", label: "Live-Out" },
                { key: "available", label: "Available now" }
            ]
        };
    },
    computed: {
        isMobile() { return window.innerWidth < 768; },
        filteredCaregivers() {
            if (!this.activeFilter) return this.caregivers;
            if (this.activeFilter === "live-in") return this.caregivers.filter(c => c.care_type === "live_in");
            if (this.activeFilter === "daily") return this.caregivers.filter(c => c.care_type === "live_out");

            if (this.activeFilter === "available") return this.caregivers.filter(c => c.available);
            return this.caregivers;
        },
        visibleCaregivers() {
            return this.filteredCaregivers.slice(0, this.visibleCount);
        },
        canViewMore() {
            return this.visibleCount < this.filteredCaregivers.length;
        },
    },
    mounted() {
        this.getCareGiverListings();
    },
    methods: {
        caregiverType(c) {
            return c.care_type === "live_out" ? "Live-Out Care" : "Live-in Care";
        },
        toggleFilter(filter) { this.activeFilter = this.activeFilter === filter ? null : filter; this.visibleCount = 8; },
        loadMore() { this.visibleCount += 4; },
        openModal(caregiver) { this.currentCaregiver = caregiver; this.showModal = true; },
        closeModal() { this.showModal = false; this.currentCaregiver = null; },
        requestCaregiver(name) {
            const phone = "2349160002370"; // 🔁 Replace with your WhatsApp number (Nigeria format)
            const msg = encodeURIComponent(`Hi, I'm interested in ${name} from Succour for care services.`);
            window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
        },
        requestFromModal() { if (this.currentCaregiver) this.requestCaregiver(this.currentCaregiver.name); },
        generateAvatar(c) {
            if (c.available) {
                return `
                    <svg viewBox="0 0 100 100">
                        <rect width="100" height="100" fill="#FEF0F2"/>
                        <circle cx="50" cy="42" r="20" fill="#C68E6C"/>
                        <ellipse cx="50" cy="82" rx="26" ry="20" fill="#17865F"/>
                        <path d="M32 28 Q34 20 50 18 Q66 20 68 28" fill="#2D2D2D"></path>
                    </svg>`;
            } else {
                return `
                <svg viewBox="0 0 100 100">
                    <rect width="100" height="100" fill="#E8F5F0"/>
                    <circle cx="50" cy="42" r="20" fill="#C68E6C"/>
                    <ellipse cx="50" cy="82" rx="26" ry="20" fill="#17865F"/>
                    <path d="M32 28 Q34 20 50 18 Q66 20 68 28" fill="#2D2D2D"></path>
                </svg>`;

            }

        },

        async getCareGiverListings() {
            this.isLoading = true;
            this.caregivers = [];

            let url = "/new-caregivers-list/";

            const fetchPage = (url) => {
                return new Promise((resolve) => {
                    const request = new Request(url);
                    request.get((data, error) => {
                        if (error) {
                            console.error("API error:", error);
                            resolve(null);
                        } else {
                            resolve(data);
                        }
                    });
                });
            };

            while (url) {
                const data = await fetchPage(url);
                if (!data) break;

                this.caregivers.push(
                    ...(data.results || []).map(c => ({
                        ...c,
                        imageError: false
                    }))
                );

                url = data.next ? data.next.replace("https://api.succour.care/v1", "") : null;
            }

            this.isLoading = false;
        }

    }
};
</script>

<style scoped>
html,
body {
    height: auto;
    overflow-y: auto;
    /* Browser scrollbar */
}



header {
    background-color: #fff8f9;
    box-shadow: 0px 4px 1px 0px #0000001a;
    position: relative;
    /* ensure shadow renders above siblings */
    z-index: 10;
}

.navbar {
    display: flex;
    justify-content: space-between;
    padding: 32px 128px;
    align-items: center;
}

.main-logo {
    width: 168px;
    height: 59px;
}

.links {
    display: flex;
    gap: 30px;
}

.join-now {
    background-color: var(--s-pink-normal);
    width: 150px;
    border-radius: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--state-white);
    text-decoration: none;
    font-size: var(--font-body-normal-regular-size);
    line-height: var(--font-body-normal-regular-line-height);
}

.normal-links {
    color: var(--s-green-normal-active);
    text-decoration: none;
    font-size: var(--font-body-normal-regular-size);
    line-height: var(--font-body-normal-regular-line-height);
    display: flex;
    align-items: center;
    transition: background-color 0.3s ease;
}



.page {
    min-height: 100vh;
    overflow-y: auto;
    /* page scroll still works */
    background: #f6f7f8;
}

/* --- Modal --- */
.modal-overlay {
    position: fixed;
    /* fixed covers entire viewport */
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.45);
    display: none;
    z-index: 1000;
}

.modal-overlay.active {
    display: block;
}

.modal-content {
    position: fixed;
    /* stays centered even when page scrolls */
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #fff;
    width: 90%;
    max-width: 480px;
    border-radius: 16px;
    max-height: 90vh;
    /* scroll if modal content too tall */
    overflow-y: auto;
    z-index: 1010;
    padding: 24px;
    /* above overlay */
    display: none;

}

.modal-content.active {
    display: block;
}

.close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 40px;
    height: 40px;
    background: rgba(23, 134, 95, 0.1);
    border-radius: 50%;
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
}

.modal-header {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
}

.modal-avatar {
    width: 100px;
    height: 100px;
    overflow: hidden;
}

.modal-info {
    flex: 1;
}

.modal-name-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.modal-name {
    font-size: 1.5rem;
    line-height: 2rem;
    font-weight: bold;
    color: #17865F;
    margin: 0;
}

.modal-type {
    font-size: 0.875rem;
    color: #6B7280;
    margin-bottom: 12px;
}

.modal-request-btn {
    padding: 0.625rem 1rem 0.625rem 1rem;
    background: #F59CAB;
    color: #fff;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-weight: bold;
    font-size: 0.875rem;
    line-height: 1.25rem;
}

.modal-details {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.detail-card {
    background: #F9FAFB;
    border-radius: 12px;
    padding: 12px;
}

.detail-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: bold;
    font-size: 14px;
    margin-bottom: 0.5rem;
}

.detail-text {
    font-size: 0.875rem;
    color: #4B5563;
    line-height: 1.25rem;
}

.page {
    position: relative;
    /* needed for overlay and modal positioning */
    min-height: 100vh;
    /* allows browser scrollbar if content exceeds viewport */
    background: #f6f7f8;
    font-family: Arial, sans-serif;
    height: auto;
    overflow-y: auto;
    /* page content scrolls */
}

.header {
    background: #fff;
    padding: 2rem;
    border-bottom: 1px solid #eee;
}

.header h1 {
    color: #17865f;
}

.filters {
    display: flex;
    gap: 10px;
    padding: 1rem;
    background: #fff;
}

.filter-btn {
    font-size: 0.875rem;
    line-height: 1.25rem;
    padding: 0.625rem 1rem;
    border-radius: 20px;
    border: 1.5px solid #F59CAB;
    color: #17865F;
    font-weight: 500;
    background: #FEF0F2;
    cursor: pointer;
}

.filter-btn.active {
    background: rgb(245, 156, 171);
    border-color: rgb(245, 156, 171);
    color: rgb(255, 255, 255);
}

/* GRID: 4 per row desktop */
.grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    padding: 1rem;
}

/* CARE CARD */
.care-card {
    background: #fff;
    border-radius: 14px;
    overflow: hidden;
    border: 1px solid #eee;
    display: flex;
    flex-direction: column;
    font-family: Arial, sans-serif;
}

.avatar-wrapper {
    position: relative;
    width: 100%;
    padding-top: 100%;
}

.avatar-svg {
    position: relative;
    width: 100%;
    height: 100%;
}

.avatar-photo-box {
    position: absolute;
    inset: 0;
    background: #FEF0F2;
    /* same as your SVG bg */
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}


.avatar-photo {
    width: 60%;
    /* shrink to match SVG "circle head" */
    height: 60%;
    /* maintain aspect ratio */
    object-fit: cover;
    /* crop properly */
    object-position: center;
    border-radius: 50%;
    /* optional: make it circular like the SVG head */
    display: block;
    margin: 0 auto;
    /* center inside the avatar box */
    top: 20%;
    /* adjust to match the circle's vertical position */
    left: 20%;
    /* adjust horizontal */
}

.avatar-photo-box-modal {
    height: 100px;
    inset: 0;
    background: #FEF0F2;
    /* same as your SVG bg */
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 50%;

}

.avatar-photo-modal {
    width: 100%;
    /* shrink to match SVG "circle head" */
    height: 100%;
    /* maintain aspect ratio */
    object-fit: cover;
    /* crop properly */
    object-position: center;
    border-radius: 50%;
    /* optional: make it circular like the SVG head */
    display: block;
    margin: 0 auto;
    /* center inside the avatar box */
    top: 20%;
    /* adjust to match the circle's vertical position */
    left: 20%;
    /* adjust horizontal */
}


.verified-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    background: #fff;
    border-radius: 12px;
    padding: 0.25rem 0.5rem 0.25rem;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
    color: #17865F;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}

.card-body {
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.card-header h3 {
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 600;
    color: #333;
    margin: 0;
}

.view-btn {
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 500;
    color: #FFFFFF;
    background: #F59CAB;
    border: none;
    border-radius: 6px;
    padding: 0.25rem 0.5rem 0.25rem;
    cursor: pointer;
}

.care-type {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: #666;
    font-weight: 500;
    margin-bottom: 0.5rem;
}

.status {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    line-height: 1rem;
}

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
}

.online {
    background: #22C55E;
    animation: pulse 2s infinite;
}

.offline {
    background: #D1D5DB;
}

.status-dot-dot {
    display: inline-block;
    width: 8px;
    /* small circle */
    height: 8px;
    border-radius: 50%;
    background-color: #22C55E;
    /* green */
    margin-right: 6px;
    vertical-align: middle;
}

.status-text {
    color: #16A34A;
}

.offline+.status-text {
    color: #9CA3AF;
}

.request-btn {
    background: #25D366;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 0;
    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 500;
    cursor: pointer;
    margin-top: 0.625rem;
    margin-bottom: 0.625rem;
}

.view-more {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
}


@media (min-width: 640px) {
    .view-more {
        margin-top: 2.5rem;
    }
}

.view-more-btn {
    padding: 1rem 2rem;
    border-radius: 9999px;
    background-color: rgb(245, 156, 171);
    color: #ffffff;
    font-weight: 600;
    font-size: 1rem;
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.15);
    border: none;
    min-height: 44px;
    margin-bottom: 2rem;
    cursor: pointer;
    transition: opacity 0.2s ease, transform 0.1s ease;
}

@media (min-width: 640px) {
    .view-more-btn {
        padding: 0.875rem 2rem;
    }
}

.view-more-btn:hover {
    opacity: 0.9;
}

.view-more-btn:active {
    transform: scale(0.97);
}

/* Add scroll indicator for long page */
.page::-webkit-scrollbar {
    width: 8px;
}

.page::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}

@keyframes pulse {
    0% {
        transform: scale(1);
        opacity: 1;
    }

    50% {
        transform: scale(1);
        opacity: 0.5;
    }

    100% {
        transform: scale(1);
        opacity: 1;
    }
}


@media (min-width: 320px) and (max-width: 768px) {
    .grid {
        grid-template-columns: repeat(2, 1fr);
        padding: 0.875rem;
        gap: 0.875rem;
    }

    .verified-badge {
        font-size: 0.625rem;
        line-height: 0.5rem;
    }

    .card-header h3 {
        font-size: 0.8rem;
        line-height: 1rem;
    }

    .view-btn {
        font-size: 0.625rem;
    }

    .main-logo {
        width: 60%;
    }

    .care-type {
        font-size: 0.75rem;
        margin-bottom: 0;
    }

    .status {
        font-size: 0.56rem;
    }

    .request-btn {
        font-size: 0.75rem;
    }

    .filter-btn {
        font-size: 0.75rem;
    }

    .navbar {
        justify-content: left;
        padding: 1rem 2rem 0.2rem;
        align-items: center;
    }

    .header {
        padding: 1rem;
        justify-content: left;
    }

    .header h1 {
        font-size: 1rem;
    }

    .avatar-photo-box-modal {
        border-radius: 50%;
    }

    .avatar-photo-modal {
        border-radius: 30%;
    }

    .modal-name {
        font-size: 1rem;
    }

    .modal-type {
        font-size: 0.75rem;
    }

    .detail-title {
        font-size: 12px;
    }

    .detail-text {
        font-size: 0.75rem;
    }

    .modal-request-btn {
        font-size: 0.75rem;
        line-height: 1rem;
        padding: 0.5rem 1rem;
    }

    .modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.45);
        display: none;
        z-index: 1000;
        height: auto;

    }

    .modal-overlay.active {
        display: block;
    }

    /* --- Modal Content: slide from bottom --- */
    .modal-content {
        position: fixed;
        left: 50%;
        top: 40%;
        bottom: 0;
        transform: translateX(-50%) translateY(100%);
        background: #fff;
        width: 100%;
        max-width: 480px;
        border-radius: 16px 16px 0 0;
        max-height: 90vh;
        overflow-y: auto;
        z-index: 1010;
        padding: 24px;
        transition: transform 0.35s ease;

    }

    /* Active state: slide UP */
    .modal-content.active {
        transform: translateX(-50%) translateY(0);
    }
}


@media(min-width: 1280px) {
    .grid {
        grid-template-columns: repeat(4, 1fr);
        padding: 2rem 4rem;
    }

    .navbar {
        padding: 2rem 4rem;
    }

    .header {
        padding: 2rem 4rem;
        font-size: 1rem;
        line-height: 1.5rem;
    }

    .filters {
        padding: 1rem 4rem;
    }
}
</style>
